// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Cairn — the generator, and it is the crux. A cairn is a pure function of (mode, seed, index)
// and is GENERATED SOLVABLE BY CONSTRUCTION, so a full clear always exists (the player is never
// dealt an impossible board) and a friend on the same seed plays the byte-identical deal.
//
// How: lay out a triangular pyramid (rows 0..h-1, row r has r+1 stones; stone (r,j) is COVERED
// by the two stones below it, (r+1,j) and (r+1,j+1); the bottom row starts exposed). Pick a
// seeded random REMOVAL ORDER — repeatedly remove a currently-exposed stone — which for a
// pyramid is always a full valid order, so generation is TOTAL and can never hang. Then assign
// runes along that order as a ±1 walk: the first stone chains off the initial scree flip (so a
// legal opening move always exists without drawing), and each later stone is either a CHAIN step
// (rune = hand ± 1, mod 13) or a DRAW-THEN-TAKE (push a scree card of rune d, stone = d ± 1).
// That walk IS a valid full-clear solution — stored as `solution` for the reference solver — and
// the number of draws it uses (D) sets the draw budget (D + slack). Scree's small slack is the
// sudden-death lever; Ramble's large slack means running out is a soft end, never a loss.

import { hashSeed, makeRng, type Rng } from '@ben-gy/game-engine/rng';
import type { Mode } from './modes';

export const RANKS = 13; // the rune ladder A,2..10,J,Q,K

export interface Stone {
  id: number;
  rank: number; // 0..12
  hue: number; // 0..3 — cosmetic only, identity is the rune
  row: number;
  col: number; // index within the row, 0..row
}

export type Action = { k: 'take'; id: number } | { k: 'draw' };

export interface Board {
  mode: Mode;
  seed: number;
  index: number;
  n: number;
  h: number;
  stones: Stone[]; // indexed by id
  coveredBy: number[][]; // coveredBy[id] = ids that must be gone before id is exposed
  stock: number[]; // ranks; stock[0] is the initial flip, stock[1..] are draws
  drawBudget: number; // max draws allowed in play
  solution: Action[]; // a guaranteed full-clear play sequence (SmartBot replays this)
  solDraws: number; // draws the solution uses (D)
}

/** Wrap a rune step into 0..12. */
export function stepRank(rank: number, dir: number): number {
  return (((rank + dir) % RANKS) + RANKS) % RANKS;
}

/** Are two runes adjacent on the wrapped ladder (one step apart)? */
export function adjacent(a: number, b: number): boolean {
  const d = (((a - b) % RANKS) + RANKS) % RANKS;
  return d === 1 || d === RANKS - 1;
}

interface Layout {
  n: number;
  coveredBy: number[][];
  rc: Array<{ row: number; col: number }>;
}

/** The fixed pyramid layout for a height (pure, deterministic — no rng). */
export function pyramidLayout(h: number): Layout {
  const idOf: number[][] = [];
  const rc: Array<{ row: number; col: number }> = [];
  let id = 0;
  for (let r = 0; r < h; r++) {
    idOf[r] = [];
    for (let j = 0; j <= r; j++) {
      idOf[r][j] = id;
      rc.push({ row: r, col: j });
      id++;
    }
  }
  const n = id;
  const coveredBy: number[][] = [];
  for (let r = 0; r < h; r++) {
    for (let j = 0; j <= r; j++) {
      const me = idOf[r][j];
      const cov: number[] = [];
      if (r + 1 < h) {
        cov.push(idOf[r + 1][j], idOf[r + 1][j + 1]);
      }
      coveredBy[me] = cov;
    }
  }
  return { n, coveredBy, rc };
}

export function buildBoard(mode: Mode, seed: number, index: number): Board {
  const rng: Rng = makeRng(hashSeed(`cairn:board:${mode.id}:${seed >>> 0}:${index}`));
  const { n, coveredBy, rc } = pyramidLayout(mode.h);

  // ── a seeded valid removal order (a topological order of the covering DAG) ──
  const present = new Array<boolean>(n).fill(true);
  const exposedNow = (id: number): boolean => coveredBy[id].every((c) => !present[c]);
  const removalOrder: number[] = [];
  while (removalOrder.length < n) {
    const cand: number[] = [];
    for (let id = 0; id < n; id++) if (present[id] && exposedNow(id)) cand.push(id);
    // a pyramid always has at least one exposed stone while any remain, so cand is never empty
    const pick = cand[Math.floor(rng() * cand.length) % cand.length];
    removalOrder.push(pick);
    present[pick] = false;
  }

  // ── assign runes + scree along that order (this walk IS a full-clear solution) ──
  const rank = new Array<number>(n).fill(0);
  const stock: number[] = [];
  const solution: Action[] = [];
  let cur = Math.floor(rng() * RANKS) % RANKS; // the initial flip rune
  stock.push(cur);
  let solDraws = 0;
  for (let step = 0; step < n; step++) {
    const sid = removalOrder[step];
    const forceChain = step === 0; // the opening move must be reachable without drawing
    if (!forceChain && rng() < mode.drawProb) {
      const d = Math.floor(rng() * RANKS) % RANKS;
      stock.push(d);
      solDraws++;
      solution.push({ k: 'draw' });
      cur = d;
    }
    const dir = rng() < 0.5 ? 1 : -1;
    rank[sid] = stepRank(cur, dir);
    cur = rank[sid];
    solution.push({ k: 'take', id: sid });
  }

  const drawBudget = solDraws + mode.slack;
  // pad the scree so the player physically has draws available up to the budget
  while (stock.length < 1 + drawBudget) stock.push(Math.floor(rng() * RANKS) % RANKS);

  const stones: Stone[] = rc.map((p, id) => ({
    id,
    rank: rank[id],
    hue: Math.floor(rng() * 4) % 4,
    row: p.row,
    col: p.col,
  }));

  return { mode, seed: seed >>> 0, index, n, h: mode.h, stones, coveredBy, stock, drawBudget, solution, solDraws };
}
